/**
 * Marketing waitlist submit contract (AUT-236).
 *
 * Layered, no Firebase and no direct Supabase from the browser:
 *   browser → POST  /api/waitlist                         (www BFF)
 *           → PATCH /api/waitlist/:referralCode/details   (www BFF)
 *           → AUTHORYTHM_API_URL/api/public/waitlist[...] (SaaS)
 *           → public.waitlist
 *
 * The BFF returns `{ unavailable: true }` when it has no SaaS origin
 * (local/CI without AUTHORYTHM_API_URL). There is no default host in code.
 */
export class WaitlistApiUnavailableError extends Error {
  override readonly name = "WaitlistApiUnavailableError"

  constructor() {
    super("Waitlist API unavailable")
  }
}

export interface WaitlistJoinResult {
  referralCode: string
  /** true on a brand-new row, false when the email was already on the list */
  created: boolean
  /** true when this email already completed the step-2 details */
  detailsCompleted: boolean
}

export interface WaitlistDetailsResult {
  referralCode: string
  detailsCompleted: boolean
}

export interface WaitlistDetailsForm {
  fullName: string
  phoneNumber?: string
  country: string
  genre: string
  hearAbout: string
}

type ApiBody = {
  referral_code?: unknown
  details_completed?: unknown
  error?: unknown
  unavailable?: unknown
}

function failureDetail(status: number, body: ApiBody, what: string) {
  return typeof body.error === "string"
    ? body.error
    : body.error != null
      ? JSON.stringify(body.error)
      : `${what} request failed (${status})`
}

export function parseWaitlistJoinResponse(status: number, body: ApiBody): WaitlistJoinResult {
  if (body.unavailable === true) {
    throw new WaitlistApiUnavailableError()
  }

  if (status < 200 || status >= 300) {
    throw new Error(failureDetail(status, body, "waitlist join"))
  }

  if (typeof body.referral_code !== "string" || body.referral_code.length === 0) {
    throw new Error(`Invalid waitlist join payload: ${JSON.stringify(body)}`)
  }

  return {
    referralCode: body.referral_code,
    created: status === 201,
    detailsCompleted: body.details_completed === true,
  }
}

export function parseWaitlistDetailsResponse(
  status: number,
  body: ApiBody,
): WaitlistDetailsResult {
  if (body.unavailable === true) {
    throw new WaitlistApiUnavailableError()
  }

  if (status < 200 || status >= 300) {
    throw new Error(failureDetail(status, body, "waitlist details"))
  }

  if (typeof body.referral_code !== "string" || body.referral_code.length === 0) {
    throw new Error(`Invalid waitlist details payload: ${JSON.stringify(body)}`)
  }

  return {
    referralCode: body.referral_code,
    detailsCompleted: body.details_completed === true,
  }
}

/** www form field names → SaaS `WaitlistDetailsSchema` field names. */
export function toPlatformDetailsBody(details: WaitlistDetailsForm) {
  const phone = details.phoneNumber?.trim()

  return {
    name: details.fullName.trim(),
    country: details.country.trim(),
    genre_preference: details.genre.trim(),
    hear_about: details.hearAbout.trim(),
    ...(phone ? { phone_number: phone } : {}),
  }
}

export async function joinWaitlist(email: string, referredBy?: string): Promise<WaitlistJoinResult> {
  const res = await fetch("/api/waitlist", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      ...(referredBy ? { referred_by: referredBy } : {}),
    }),
  })

  const body = (await res.json()) as ApiBody

  return parseWaitlistJoinResponse(res.status, body)
}

export async function completeWaitlistDetails(
  referralCode: string,
  details: WaitlistDetailsForm,
): Promise<WaitlistDetailsResult> {
  const res = await fetch(`/api/waitlist/${encodeURIComponent(referralCode)}/details`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(toPlatformDetailsBody(details)),
  })

  const body = (await res.json()) as ApiBody

  return parseWaitlistDetailsResponse(res.status, body)
}
